import { Directive, inject, Input, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { debounceTime, Observable, Subscription, switchMap, tap } from 'rxjs';
import { filter } from 'rxjs/operators';
import { CitiesService } from 'src/app/services/cities.service';
import { AddressHint } from 'src/app/api/v1/models';
import { FormType } from 'src/app/shared/interfaces/form-type';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { FormStepAbstract } from '../../../../../shared/components/abstract/form-step.abstract';
import { AlphanumericValidator, FlatValidator } from '../../../../../shared/validators/text.validators';
import { FormSaveStrategy } from '../../../strategy/form-save-strategy/form-save-strategy.abstract';
import { AddressSelectorService } from './address-selector.service';
import { OrderDeliveryModalService } from '../../../../../components/order-delivery-modal/order-delivery-modal.service';
import { SessionRepository } from '../../../../../shared/repositories/session.repository';

interface IHintAddressControl {
  line1: string | null,
  city: string | null,
  street: string | null,
  house: string | null,
  flat: string | null,
  porch: string | null,
  floor: string | null,
}

@UntilDestroy()
@Directive()
export class HintAddressStepAbstract extends FormStepAbstract implements OnInit {
  @Input() isFullView: boolean = true;

  private citiesService: CitiesService = inject(CitiesService);
  private addressSelectorService: AddressSelectorService = inject(AddressSelectorService);
  private formSaveStrategy: FormSaveStrategy = inject(FormSaveStrategy);
  private orderDeliveryModal: OrderDeliveryModalService = inject(OrderDeliveryModalService);
  private sessionRepository: SessionRepository = inject(SessionRepository);

  override form: FormGroup<FormType<IHintAddressControl>> = new FormGroup<FormType<IHintAddressControl>>({
    line1: new FormControl<string | null>('', [Validators.required]),
    city: new FormControl<string | null>('', [Validators.required]),
    street: new FormControl<string | null>(''),
    house: new FormControl<string | null>('', [Validators.required]),
    flat: new FormControl('', {validators: [Validators.maxLength(15), FlatValidator], nonNullable: true}),
    porch: new FormControl('', {validators: [Validators.maxLength(10), AlphanumericValidator], nonNullable: true}),
    floor: new FormControl('', {validators: [Validators.maxLength(10), AlphanumericValidator], nonNullable: true}),
  });

  searchControl: FormControl<string> = new FormControl('', {nonNullable: true});

  isLoadingHints$: Observable<boolean> = this.citiesService.isLoadingHints$;
  isShowMessageHint: boolean = true;

  hints$: Observable<AddressHint[]> = this.citiesService.hints$.pipe(
    tap(hints => this.isShowMessageHint = hints.length <= 0)
  );

  selectedHint: AddressHint | null = null;

  deliverySub: Subscription;

  override ngOnInit(): void {
    super.ngOnInit();

    this.orderDeliveryModal.isShowOrderTypeModal$.pipe(
      filter(isShow => !isShow),
      switchMap(() => this.sessionRepository.deliveryAddress$),
      untilDestroyed(this)
    ).subscribe((deliveryAddress) => {
      if(!deliveryAddress) {
        return;
      }
      this.searchControl.setValue(deliveryAddress.street + ', ' + deliveryAddress.house);
      this.form.patchValue(deliveryAddress, {emitEvent: false});
    });

    this.addressSelectorService.deliveryAddress$.pipe(
      tap(deliveryAddress => {
        if(!deliveryAddress || !deliveryAddress.line1) {
          this.form.reset();
          return;
        }

        this.searchControl.setValue(deliveryAddress.street + ', ' + deliveryAddress.house);
        this.form.patchValue(deliveryAddress);
      }),
      untilDestroyed(this)
    ).subscribe();

    this.subscribeToDeliveryZone();
  }

  private subscribeToDeliveryZone(): void {
    this.deliverySub = this.form.valueChanges.pipe(
      debounceTime(this.formSaveStrategy.debounceTimeout),
      filter(formValue => !!formValue.line1 && !!formValue.house && !!formValue.city),
      switchMap((formValue) => this.formSaveStrategy.setDeliveryAddress(formValue)),
      tap((formValue) => {
        this.citiesService.setDeliveryAddress(formValue);
      }),
    ).subscribe();
  }

  onSearchAddress(value: string): void {
    this.citiesService.setHintSearchString(value);
  }

  onSelectAddress(hint: AddressHint): void {
    this.selectedHint = hint;

    if(!hint.city || !hint.house) {
      this.isError = true;
      return;
    }

    this.isError = false;

    this.form.controls.line1.setValue(hint.value);
    this.form.controls.city.setValue(hint.city || '');
    this.form.controls.street.setValue(hint.street || '');
    this.form.controls.house.setValue(hint.house || '');
  }
}
